import { useState } from 'react';
import { Network, Scaling } from 'lucide-react';
import { Section, SplitView } from '@vnetwork/ui/layout';
import { StatusPill, DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Card, CardBody, CardHeader, CardTitle, Input, Button, Skeleton, Badge, Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useClusterIngressDetail,
  useResizeIngressLbFlavor,
  useSnapshotSchedule,
  useSetSnapshotSchedule,
  type HaProxyStatGroup,
} from '@/api/k8s';
import { useLoadBalancerMonitoring } from '@/api/opennebula';
import { IngressGatewayPicker } from './IngressGatewayPicker';
import { IngressPortForwarding } from './IngressPortForwarding';
import { LoadBalancerResize } from '@/pages/services/compute/loadbalancers/LoadBalancerResize';
import { LbStats } from '@/pages/services/compute/loadbalancers/LbStats';
import { MonitoringTab } from '@/pages/services/compute/instances/MonitoringTab';
import { DescList, DescItem, monoValue } from '../../components/DescList';
import { CompactStat, CompactStatGroup } from '../../components/CompactStat';
import { formatBytes } from '@/pages/services/_shared/analytics-format';
import { toneColor } from '@/pages/services/shared';

export { MetricsTab } from './ClusterMetrics';

// Shared inline styles for the ingress panels.
const subHead: React.CSSProperties = { fontWeight: 'var(--vn-font-weight-semibold)' as React.CSSProperties['fontWeight'], fontSize: 'var(--vn-font-size-sm)' };
const mutedSm: React.CSSProperties = { color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' };
const num: React.CSSProperties = { color: 'var(--vn-fg-muted)', fontVariantNumeric: 'tabular-nums' };


interface HaServerRow {
  server_name?: string;
  name?: string;
  status?: string;
  session_current?: number | null;
  session_total?: number | null;
  byte_in?: number | null;
  byte_out?: number | null;
}

const serverUp = (s: HaServerRow) => /up/i.test(s.status ?? '');
const serverDown = (s: HaServerRow) => /down/i.test(s.status ?? '');

const haServerCols: ColumnDef<HaServerRow, unknown>[] = [
  { id: 'server', header: 'Server', cell: ({ row }) => <code style={{ ...monoValue, fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'], whiteSpace: 'nowrap' }}>{row.original.server_name || row.original.name || '—'}</code> },
  { id: 'status', header: 'Status', cell: ({ row }) => <StatusPill tone={serverUp(row.original) ? 'success' : serverDown(row.original) ? 'danger' : 'muted'}>{row.original.status || '—'}</StatusPill> },
  { id: 'sessions', header: 'Sessions (cur / total)', cell: ({ row }) => <span style={num}>{(row.original.session_current ?? 0).toLocaleString()} / {(row.original.session_total ?? 0).toLocaleString()}</span> },
  { id: 'in', header: 'In', cell: ({ row }) => <span style={num}>{formatBytes(row.original.byte_in)}</span> },
  { id: 'out', header: 'Out', cell: ({ row }) => <span style={num}>{formatBytes(row.original.byte_out)}</span> },
];

// ---- Ingress detail ----
export function IngressDetailTab({ serviceId, clusterId }: { serviceId: string; clusterId: string }) {
  const { data, isLoading } = useClusterIngressDetail(serviceId, clusterId);
  // The ingress LB is a compute (s10003) instance; resize runs against its owning
  // compute service, then we persist the chosen flavor onto the cluster spec.
  const lb = data?.lb;
  const computeSvc = data?.compute_service_uuid;
  const persistFlavor = useResizeIngressLbFlavor(serviceId, clusterId);
  const [resizing, setResizing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const lbFlavor = lb?.metadata?.flavor || data?.ingress_lb_flavor;
  const lbCores = lb?.metadata?.resources?.cores;
  const lbMem = lb?.metadata?.resources?.memory;
  const svc = data?.ingress;
  const lbIp = svc?.status?.loadBalancer?.ingress?.[0]?.ip || svc?.status?.loadBalancer?.ingress?.[0]?.hostname;
  const ports = svc?.spec?.ports ?? [];
  // The endpoint returns `stats` as a SINGLE traffic group object (or an array of
  // them); normalise to an array so one group still renders.
  const stats: HaProxyStatGroup[] = Array.isArray(data?.stats)
    ? (data!.stats as HaProxyStatGroup[])
    : data?.stats
      ? [data.stats as HaProxyStatGroup]
      : [];

  // Backend proxy groups with their server rows (lb_stats is the load balancer's own
  // frontend rollup — skip it, it has no Server rows worth showing).
  const groups = stats
    .filter((g) => g.proxy_name !== 'lb_stats' && g.values?.some((v) => v.title === 'Server'))
    .map((g) => ({
      name: g.proxy_name || 'Backend',
      servers: (g.values?.filter((v) => v.title === 'Server') ?? []) as HaServerRow[],
    }));

  // Page-level rollup for the KPI strip.
  const allServers = groups.flatMap((g) => g.servers);
  const upCount = allServers.filter(serverUp).length;
  const anyDown = allServers.some(serverDown);
  const allDown = allServers.length > 0 && allServers.every(serverDown);
  const sessions = allServers.reduce((s, v) => s + (v.session_current ?? 0), 0);
  const bytesIn = allServers.reduce((s, v) => s + (v.byte_in ?? 0), 0);
  const bytesOut = allServers.reduce((s, v) => s + (v.byte_out ?? 0), 0);
  const controllerLabel = allDown ? 'Down' : anyDown ? 'Degraded' : 'Up';
  const controllerColor = toneColor(allDown ? 'danger' : anyDown ? 'warning' : 'success');

  if (isLoading) {
    return (
      <Section title="Ingress" description="Ingress controller service.">
        <Skeleton style={{ height: 200 }} />
      </Section>
    );
  }
  if (!lb && !svc) {
    return (
      <Section
        title="Ingress"
        description="Ingress controller service."
        actions={<Button variant="secondary" startIcon={<Network size={15} />} onClick={() => setPickerOpen(true)}>Select gateway</Button>}
      >
        {pickerOpen && <IngressGatewayPicker serviceId={serviceId} clusterId={clusterId} onClose={() => setPickerOpen(false)} />}
        <Alert tone="info" title="No ingress load balancer">
          This cluster has no ingress controller exposed as a LoadBalancer service. Deploy one (or enable the NGINX add-on); if you run more than one controller, use “Select gateway” to choose which load balancer to view.
        </Alert>
      </Section>
    );
  }

  // Rail: the controller service identity + its exposed ports — reference info,
  // kept out of the main column so the traffic panels lead (mirrors Overview).
  const rail = (
    <>
      {lb && (
        <Card flat>
          <CardHeader><CardTitle style={{ fontSize: 'var(--vn-font-size-md)' }}>Load balancer</CardTitle></CardHeader>
          <CardBody>
            <DescList minColumn={999} style={{ gap: 'var(--vn-space-3)' }}>
              <DescItem label="Size">{lbFlavor ? <code style={monoValue}>{lbFlavor}</code> : '—'}</DescItem>
              <DescItem label="Resources">{lbCores ? `${lbCores} vCPU · ${lbMem} GB` : '—'}</DescItem>
              <DescItem label="Status">{(() => { const s = lb.status || ''; return s ? <StatusPill tone={/powered_on|active|running/i.test(s) ? 'success' : 'muted'}>{s.charAt(0).toUpperCase() + s.slice(1)}</StatusPill> : '—'; })()}</DescItem>
            </DescList>
            <p style={{ ...mutedSm, marginTop: 'var(--vn-space-3)' }}>Resizing briefly powers off the load balancer — ingress traffic pauses during the swap.</p>
            <Button
              variant="secondary"
              startIcon={<Scaling size={15} />}
              disabled={!computeSvc || resizing}
              onClick={() => setResizing(true)}
              style={{ marginTop: 'var(--vn-space-3)', width: '100%' }}
            >
              Resize
            </Button>
          </CardBody>
        </Card>
      )}

      {svc && (
        <Card flat>
          <CardHeader><CardTitle style={{ fontSize: 'var(--vn-font-size-md)' }}>Controller service</CardTitle></CardHeader>
          <CardBody>
            <DescList minColumn={999} style={{ gap: 'var(--vn-space-3)' }}>
              <DescItem label="Service"><code style={monoValue}>{svc.metadata?.name || '—'}</code></DescItem>
              <DescItem label="Namespace"><code style={monoValue}>{svc.metadata?.namespace || '—'}</code></DescItem>
              <DescItem label="Type">{svc.spec?.type ? <Badge tone="info" appearance="subtle" size="sm">{svc.spec.type}</Badge> : '—'}</DescItem>
              <DescItem label="External IP">{lbIp ? <code style={monoValue}>{lbIp}</code> : '—'}</DescItem>
              <DescItem label="Cluster IP"><code style={monoValue}>{svc.spec?.clusterIP || '—'}</code></DescItem>
            </DescList>
          </CardBody>
        </Card>
      )}

      {ports.length > 0 && (
        <Card flat>
          <CardHeader><CardTitle style={{ fontSize: 'var(--vn-font-size-md)' }}>Ports</CardTitle></CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {ports.map((p, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
                <Badge tone="neutral" appearance="outline" size="sm">{p.name || p.protocol || 'port'}</Badge>
                <code style={monoValue}>{p.port} → {String(p.targetPort)}</code>
                {p.nodePort ? <span style={{ ...mutedSm, marginLeft: 'auto' }}>node {p.nodePort}</span> : null}
              </div>
            ))}
          </CardBody>
        </Card>
      )}
    </>
  );

  return (
    <Section
      title="Ingress"
      description="Ingress controller service."
      actions={<Button variant="secondary" startIcon={<Network size={15} />} onClick={() => setPickerOpen(true)}>Change gateway</Button>}
    >
      {pickerOpen && <IngressGatewayPicker serviceId={serviceId} clusterId={clusterId} onClose={() => setPickerOpen(false)} />}
      {resizing && lb && computeSvc && (
        <LoadBalancerResize
          serviceId={computeSvc}
          lb={lb}
          onClose={() => setResizing(false)}
          onResized={async (flavor) => { await persistFlavor.mutateAsync({ flavor }); }}
        />
      )}
      <SplitView asideWidth="300px" aside={<div style={{ display: 'grid', gap: 'var(--vn-space-4)', alignContent: 'start' }}>{rail}</div>}>
        <Tabs defaultValue={lb && computeSvc ? 'stats' : 'backends'}>
          <TabsList>
            {lb && computeSvc && <TabsTrigger value="stats">Statistics</TabsTrigger>}
            {lb && computeSvc && <TabsTrigger value="monitoring">Monitoring</TabsTrigger>}
            {lb && computeSvc && <TabsTrigger value="forwarding">Port forwarding</TabsTrigger>}
            {/* Control-plane load balancer (apiserver/etcd/konnectivity) from the cluster
                endpoint — distinct from the ingress data-plane shown under Statistics. */}
            <TabsTrigger value="backends">Control plane</TabsTrigger>
          </TabsList>

          <TabsContent value="backends">
        {/* Traffic KPIs — controller rollup at a glance, before the per-group tables. */}
        <Card flat style={{ marginBottom: 'var(--vn-space-4)' }}>
          <CardBody>
            <CompactStatGroup columns={4}>
              <CompactStat label="Controller" value={controllerLabel} dotColor={controllerColor} />
              <CompactStat label="Backend servers" value={`${upCount}/${allServers.length}`} hint="up" />
              <CompactStat label="Sessions" value={sessions.toLocaleString()} hint="current" />
              <CompactStat label="Traffic" value={`${formatBytes(bytesIn)} → ${formatBytes(bytesOut)}`} hint="in / out" />
            </CompactStatGroup>
          </CardBody>
        </Card>

        {/* One panel per proxy group: name + rollup pill on the left,
            servers-up / live-session meta on the right, table in its own frame. */}
        {groups.map((group) => {
          const gUp = group.servers.filter(serverUp).length;
          const gDown = group.servers.some(serverDown);
          const gAllDown = group.servers.length > 0 && group.servers.every(serverDown);
          const gSessions = group.servers.reduce((s, v) => s + (v.session_current ?? 0), 0);
          return (
            <div key={group.name} style={{ marginBottom: 'var(--vn-space-5)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', marginBottom: 'var(--vn-space-2)' }}>
                <span style={subHead}>{group.name}</span>
                <StatusPill tone={gAllDown ? 'danger' : gDown ? 'warning' : 'success'}>{gAllDown ? 'Down' : gDown ? 'Degraded' : 'Up'}</StatusPill>
                <span style={{ ...mutedSm, marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>
                  {gUp}/{group.servers.length} up · {gSessions.toLocaleString()} sessions
                </span>
              </div>
              <DataTable columns={haServerCols} data={group.servers} getRowId={(s, i) => `${s.server_name ?? s.name ?? i}`} />
            </div>
          );
        })}

        {groups.length === 0 && (
          <EmptyState
            icon={<Network size={20} />}
            title="No backend stats yet"
            description="Traffic stats appear once the load balancer starts receiving traffic."
          />
        )}
          </TabsContent>

          {lb && computeSvc && (
            <TabsContent value="monitoring">
              <MonitoringTab
                serviceId={computeSvc}
                instance={lb}
                useMonitoring={useLoadBalancerMonitoring}
                description="CPU, memory, disk and network for this ingress load balancer."
              />
            </TabsContent>
          )}

          {lb && computeSvc && (
            <TabsContent value="stats">
              <LbStats serviceId={computeSvc} lbId={lb.id} />
            </TabsContent>
          )}

          {lb && computeSvc && (
            <TabsContent value="forwarding">
              <IngressPortForwarding serviceId={serviceId} clusterId={clusterId} computeSvc={computeSvc} lb={lb} />
            </TabsContent>
          )}
        </Tabs>
      </SplitView>
    </Section>
  );
}
// ---- Snapshot schedule (cron + retention) ----
export function SnapshotScheduleCron({ serviceId, clusterId }: { serviceId: string; clusterId: string }) {
  const { show } = useToast();
  const { data: schedule } = useSnapshotSchedule(serviceId, clusterId);
  const [cron, setCron] = useState('');
  const [retain, setRetain] = useState('');
  // Real fields are `cron` and `retention`; `frequency`/`retain` kept as fallbacks.
  const curCron = cron || schedule?.cron || schedule?.frequency || '0 2 * * *';
  const curRetain = retain || String(schedule?.retention ?? schedule?.retain ?? 5);
  const enabled = schedule?.enabled;

  const save = useSetSnapshotSchedule(serviceId, clusterId, {
    onSuccess: () => show({ tone: 'success', title: 'Schedule saved' }),
    onError: () => show({ tone: 'danger', title: 'Save failed' }),
  });

  const control = (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      <div style={{ display: 'flex', gap: 'var(--vn-space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <Field label="Cron expression">
            {(p) => <Input {...p} value={curCron} onChange={(e) => setCron(e.target.value)} placeholder="0 2 * * *" style={{ fontFamily: 'var(--vn-font-mono)' }} />}
          </Field>
        </div>
        <div style={{ flex: '0 0 140px' }}>
          <Field label="Retention (days)">
            {(p) => <Input {...p} type="number" min={1} value={curRetain} onChange={(e) => setRetain(e.target.value)} />}
          </Field>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
        <Button disabled={!curCron || save.isPending} onClick={() => save.mutate({ enabled: true, cron: curCron, retention: Number(curRetain) })}>Save schedule</Button>
        {enabled && <Button variant="secondary" disabled={save.isPending} onClick={() => save.mutate({ enabled: false })}>Disable</Button>}
      </div>
    </div>
  );

  // Same Card shell as the other cluster-detail panels (e.g. Pod distribution):
  // titled header with a status Badge in the actions slot, form in the body.
  return (
    <Card>
      <CardHeader actions={<Badge tone={enabled ? 'success' : 'neutral'} appearance="subtle" size="sm">{enabled ? 'Enabled' : 'Disabled'}</Badge>}>
        <CardTitle style={{ fontSize: 'var(--vn-font-size-md)' }}>Snapshot schedule</CardTitle>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <p style={{ ...mutedSm, margin: 0 }}>Automatic recurring cluster snapshots. Standard cron: minute hour day-of-month month day-of-week. Default <code>0 2 * * *</code> = daily at 02:00.</p>
          {control}
        </div>
      </CardBody>
    </Card>
  );
}
